import { createContext, useCallback, useContext, useMemo, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { queryKeys } from './queryClient';
import { authService } from '@/services/auth.service';
import { securityService } from '@/services/security.service';
import { ApiRequestError } from '@/services/api';
import type { CurrentUser } from '@/types/schemas';
import type { PermissionCode } from '@/types/permission';

interface AuthValue {
  user: CurrentUser | null;
  loading: boolean;
  can: (permission: PermissionCode | string) => boolean;
  login: (email: string, password: string) => Promise<CurrentUser>;
  /** The passkey ceremony, ending in exactly the session a password login gives. */
  loginWithPasskey: () => Promise<CurrentUser>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthValue | null>(null);

/**
 * Whether this browser has an established session.
 *
 * The session cookies are HttpOnly and unreadable by design; the CSRF cookie is
 * deliberately readable and is set alongside them, so its presence is the only
 * honest signal available here. It grants nothing on its own.
 */
function hadSession(): boolean {
  return document.cookie.includes('ramenten_csrf=');
}

/**
 * Session state comes from GET /auth/me over an HttpOnly cookie. There is no
 * token in localStorage, sessionStorage or memory — JS cannot read it at all.
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const client = useQueryClient();

  const { data, isPending } = useQuery({
    queryKey: queryKeys.me,
    queryFn: async ({ signal }) => {
      try {
        return await authService.me(signal);
      } catch (error) {
        // A 401 means one of two different things. Before login there is no
        // session at all, and asking to refresh one is pointless noise — the
        // console filled with "/auth/refresh 401" for exactly that reason.
        // Once signed in, the readable CSRF cookie marks that a session was
        // established, so a 401 there means only the short-lived access cookie
        // aged out: rotate once and ask again.
        if (error instanceof ApiRequestError && error.isUnauthorized && hadSession()) {
          await authService.refresh();
          return authService.me(signal);
        }
        throw error;
      }
    },
    retry: false,
    staleTime: 5 * 60_000,
  });

  const loginMutation = useMutation({
    mutationFn: ({ email, password }: { email: string; password: string }) =>
      authService.login(email, password),
    onSuccess: (user) => client.setQueryData(queryKeys.me, user),
  });

  const passkeyLoginMutation = useMutation({
    mutationFn: () => securityService.loginWithPasskey(),
    onSuccess: (user) => client.setQueryData(queryKeys.me, user),
  });

  const logoutMutation = useMutation({
    mutationFn: () => authService.logout(),
    onSettled: () => {
      client.setQueryData(queryKeys.me, null);
      client.clear();
    },
  });

  const user = data ?? null;

  const can = useCallback(
    (permission: PermissionCode | string) => Boolean(user?.permissions.includes(permission)),
    [user],
  );

  const value = useMemo<AuthValue>(
    () => ({
      user,
      loading: isPending,
      can,
      login: async (email, password) => loginMutation.mutateAsync({ email, password }),
      loginWithPasskey: async () => passkeyLoginMutation.mutateAsync(),
      logout: async () => {
        await logoutMutation.mutateAsync().catch((error: unknown) => {
          // A failed logout must still clear the client session.
          if (!(error instanceof ApiRequestError)) throw error;
        });
      },
    }),
    [user, isPending, can, loginMutation, passkeyLoginMutation, logoutMutation],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthValue {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used inside AuthProvider');
  return context;
}
